import React from 'react';
import ReactDOM from 'react-dom';
import DynamicNav from '../components/DynamicNav';
import { CollectionPageContext, PageContext } from '../frontend/pageContext';
import Header from '../components/Header';
import BannerHistory from '../components/BannerHistory';
import { setMenuExpanded, decorateClientMenu } from './utils';
import PageNavButtons from '../components/PageNavButtons';
import HomepageHeaderButtons from '../components/HomepageHeaderButtons';
import Collection from '../components/Collection';
import TableOfContents from '../components/TableOfContents';
import { registerCopyCodeToClipboard } from './copyCodeToClipboard';
import { registerAnchorScroll } from './anchorScroll';

// Save menu scroll position and set before any dom is displayed (if this were in useEffect, there would be a flashing)
const menuElement = document.getElementById('js-menu');
const contentAreaEl = document.querySelector<HTMLElement>('.content-area');
const footerEl = document.querySelector<HTMLElement>('.suds-footer');
const breadcrumbsEl = document.querySelector<HTMLElement>('.breadcrumb_list');
const bannerSectionsEl =
  document.querySelector<HTMLElement>('.banners-section');
const asideEl = document.querySelector<HTMLElement>('aside');

function setPosition() {
  if (contentAreaEl) {
    contentAreaEl.style.marginLeft = '';
    if (breadcrumbsEl) {
      const breadcrumbX = `${breadcrumbsEl.getBoundingClientRect().bottom}px`;
      contentAreaEl.style.top = breadcrumbX;
      if (bannerSectionsEl) {
        bannerSectionsEl.style.top = breadcrumbX;
        if (asideEl) {
          asideEl.style.top = `${bannerSectionsEl.getBoundingClientRect().bottom + 10}px`;
        }
      }
    }
  }
}

setPosition();

registerCopyCodeToClipboard();
registerAnchorScroll();

// Set menu scroll position
if (window.sessionStorage.menuScrollPos) {
  if (menuElement) {
    menuElement.scrollTop = Number(
      window.sessionStorage.getItem('menuScrollPos'),
    );
  }
  window.sessionStorage.removeItem('menuScrollPos');
}

// Save menu scroll position on page unload
window.onbeforeunload = () => {
  if (menuElement) {
    window.sessionStorage.setItem(
      'menuScrollPos',
      String(menuElement.scrollTop),
    );
  }
};

// Reset the menu width and margin/offset for the content when the window
// is resized below 1024px. This prevents strange behaviors when the page
// switches over to the mobile menu.
//
// The style attributes are set directly on the elements by even listeners
// in `Sidebar.tsx`.
window.addEventListener('resize', function () {
  if (window.innerWidth <= 1024) {
    document.querySelectorAll<HTMLElement>('.menu').forEach(function (el) {
      el.style.width = '';
    });
    if (footerEl) {
      footerEl.style.width = '';
    }
  }
  setPosition();
});

/*
Note this is the hydration for the dynamic sidebar
https://confluence.silabs.com/pages/viewpage.action?pageId=122855251
*/
const hydrateMenu = async () => {
  const pathname = window.location.pathname;

  const {
    menu,
    docSpace,
    docLeaf,
    spacePdfExists,
    relPath,
    isStatic,
    tagSet,
    toc,
    versionsHistory,
    leafPdfExists,
  } = JSON.parse((window as any).__SUDS_DATA__);

  try {
    await decorateClientMenu(
      menu,
      docSpace,
      docLeaf,
      pathname,
      relPath,
      isStatic,
    );
    setMenuExpanded(menu);
  } catch (err) {
    throw new Error(`Error building menu ${err}`);
  }

  const checkToc = document.getElementById('toc-root');
  if (checkToc) {
    ReactDOM.hydrate(
      <PageContext.Provider
        value={{
          isStatic,
          pdfMode: false,
          spacePdfExists,
          relPath,
          docSpace,
          docLeaf,
          tagSet,
          pathname,
          toc,
        }}
      >
        <TableOfContents />
        <div
          className="light_theme"
          ub-in-page="634f96f53042a864e44b82f8"
        ></div>
        <div
          className="dark_theme"
          ub-in-page="699fe6e9dac31e032209597c"
        ></div>
      </PageContext.Provider>,
      document.getElementById('toc-root'),
    );
  }

  ReactDOM.hydrate(
    <PageContext.Provider
      value={{
        isStatic,
        pdfMode: false,
        spacePdfExists,
        relPath,
        docSpace,
        docLeaf,
        tagSet,
        pathname,
        toc,
      }}
    >
      <DynamicNav menu={menu} />
    </PageContext.Provider>,
    document.getElementById('dyn-nav'),
  );

  ReactDOM.hydrate(
    <PageContext.Provider
      value={{
        isStatic,
        pdfMode: false,
        spacePdfExists,
        relPath,
        docSpace,
        docLeaf,
        tagSet,
        pathname,
        toc,
      }}
    >
      <Header />
    </PageContext.Provider>,
    document.getElementById('header'),
  );

  const checkBanner = document.getElementById('banner');
  if (checkBanner) {
    ReactDOM.hydrate(
      <PageContext.Provider
        value={{
          isStatic,
          pdfMode: false,
          spacePdfExists,
          relPath,
          docSpace,
          docLeaf,
          pathname,
          versionsHistory,
          leafPdfExists,
        }}
      >
        <BannerHistory />
      </PageContext.Provider>,
      document.getElementById('banner'),
    );
  }

  const checkNavButton = document.getElementById('page-nav-buttons');
  if (checkNavButton) {
    ReactDOM.hydrate(
      <PageContext.Provider
        value={{
          isStatic,
          pdfMode: false,
          spacePdfExists,
          relPath,
          docSpace,
          docLeaf,
          pathname,
          versionsHistory,
          leafPdfExists,
        }}
      >
        <PageNavButtons menu={menu} />
      </PageContext.Provider>,
      document.getElementById('page-nav-buttons'),
    );
  }
};
const hydrate = (window as any).__HYDRATE__;
const hydrateHomepageComponent = (window as any).__HYDRATEHOMEPAGE__;

if (hydrate) {
  // Top level await would be nice
  hydrateMenu()
    .then(() => {
      console.log('menu success');
    })
    .catch((err) => {
      console.log('menu error: ', err);
    });
}

const hydrateHeaderButtons = async () => {
  const { items } = JSON.parse((window as any).__SUDS_DATA__);
  ReactDOM.hydrate(
    <HomepageHeaderButtons />,
    document.getElementById('header-buttons'),
  );

  ReactDOM.hydrate(
    <CollectionPageContext.Provider value={{ items }}>
      <Collection />
    </CollectionPageContext.Provider>,
    document.getElementById('collection-items'),
  );

  // Add search toggle functionality
  const searchBtn = document.querySelector('[data-search-btn]');
  if (searchBtn) {
    let isSearchOpen = false;

    searchBtn.addEventListener('click', () => {
      isSearchOpen = !isSearchOpen;
      const useElement = searchBtn.querySelector('svg use');
      searchBtn.setAttribute('data-search-btn', isSearchOpen.toString());
      if (useElement) {
        useElement.setAttribute(
          'href',
          isSearchOpen
            ? '/assets/images/icons/fa-times.svg'
            : '/assets/images/icons/fa-search.svg',
        );
      }
    });
  }
};

if (hydrateHomepageComponent) {
  hydrateHeaderButtons()
    .then(() => {
      console.log('header buttons success');
    })
    .catch((err) => {
      console.log('header buttons error: ', err);
    });
}
